/**
 * Shared form-control tokens — Input, Select, SearchableSelect, DatePicker, etc.
 * Changing these updates every consumer that imports them.
 */

/** 44px — Tailwind `h-11` */
export const FORM_CONTROL_HEIGHT_CLASS = "h-11";

/**
 * 36px — Tailwind `h-9`. The compact form control, for dense surfaces: toolbar
 * filters, inline controls inside a card header, anywhere a 44px field would
 * dominate the row it sits in.
 *
 * Reach for it through a component's `size="sm"` prop, never by passing `h-9`
 * in `className`: the height token is applied AFTER the consumer's classes so
 * it cannot be shrunk by accident, which also means a local `h-9` is silently
 * discarded by tailwind-merge.
 */
export const FORM_CONTROL_HEIGHT_SM_CLASS = "h-9";

/** 14px — Tailwind `text-sm`; keep Input / Select / dropdown triggers consistent */
export const FORM_CONTROL_TEXT_CLASS = "text-sm";

/**
 * Editable form field titles (Design Title, School, filter labels, etc.).
 * 13px · semibold · `text-field-label` (foreground).
 *
 * For read-only detail captions use {@link DETAIL_LABEL_CLASS} or
 * `ReadOnlyMetaField`.
 */
export const FORM_FIELD_LABEL_CLASS =
  "text-[13px] font-semibold leading-5 text-field-label";

/**
 * Caption in a read-only title/value pair — 13px normal, `text-detail-label`.
 * Not for form fields; use {@link FORM_FIELD_LABEL_CLASS} for editable labels.
 * Pair with {@link DETAIL_VALUE_CLASS} (or {@link DETAIL_VALUE_EMPTY_CLASS}).
 */
export const DETAIL_LABEL_CLASS =
  "text-[13px] font-normal leading-5 text-detail-label";

/** 12px read-only label — horizontal rows (deal summary, order detail sidebar). */
export const DETAIL_LABEL_COMPACT_CLASS =
  "text-xs font-normal leading-5 text-detail-label";

/** 12px detail value — horizontal rows beside a compact label. */
export const DETAIL_VALUE_COMPACT_CLASS =
  "text-xs font-medium leading-5 text-detail-value";

/**
 * Read-only field captions in detail/summary panels (Title, Date Requested,
 * Style Code, Designer, …). Alias of {@link DETAIL_LABEL_CLASS}.
 */
export const READ_ONLY_FIELD_LABEL_CLASS = DETAIL_LABEL_CLASS;

/** Value in a read-only title/value pair — 14px medium, Dark Gray 500. */
export const DETAIL_VALUE_CLASS =
  "text-sm font-medium leading-5 text-detail-value";

/** Empty / em-dash placeholder when a read-only value is missing. */
export const DETAIL_VALUE_EMPTY_CLASS =
  "text-sm font-normal leading-5 text-detail-value-empty";

/**
 * Panel group headings inside filter sheets when nesting fields
 * (e.g. Pipeline “Account” → Account type / School).
 * Same size as field titles, but **semibold** · `text-foreground`.
 */
export const PANEL_GROUP_HEADING_CLASS =
  "text-[13px] font-semibold leading-5 text-foreground";

/**
 * Section / card headings (Licensing Info, Estimate Quantity, Design Inspiration).
 * 14px · semibold · `text-foreground`.
 */
export const SECTION_HEADING_CLASS =
  "text-sm font-semibold leading-5 text-foreground";

/**
 * Typed / selected / read-only value text — normal weight, dark foreground.
 * Pair with `FORM_CONTROL_TEXT_CLASS` (`text-sm`) on Input / Select / Textarea /
 * DatePicker / SearchableSelect / CatalogAsyncSelect so every form value
 * matches (Sales Pipeline, Accounts, Users, Design Requests, …).
 * Placeholders use `text-field-placeholder` (Light Gray 500) — lighter than
 * helper copy (`text-field-helper` / Light Gray 600). Does not apply to radios or checkboxes.
 */
export const FORM_CONTROL_VALUE_CLASS = "font-normal text-field-foreground";

/**
 * `type="number"` — hide native spinners; use tabular figures. Input text is left-aligned unless a field passes an explicit alignment class.
 */
export const FORM_CONTROL_NUMBER_INPUT_CLASS =
  "tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

/**
 * Pair with FORM_CONTROL_PLACEHOLDER_CLASS, FORM_CONTROL_FOCUS_RING_CLASS, etc.
 */
export const FORM_CONTROL_SURFACE_CLASS =
  "border border-field-border bg-field text-field-foreground";

/** Outline / segmented control border — Toggle outline, OTP cells, etc. */
export const FORM_CONTROL_OUTLINE_SURFACE_CLASS =
  "border border-field-border bg-transparent";

/** Empty combobox / select trigger before a value is chosen. */
export const FORM_CONTROL_EMPTY_VALUE_CLASS = "text-field-placeholder";

/** CSS `::placeholder` and Radix Select `data-[placeholder]` empty state. */
export const FORM_CONTROL_PLACEHOLDER_CLASS =
  "placeholder:font-normal placeholder:opacity-100 placeholder:text-field-placeholder data-[placeholder]:font-normal data-[placeholder]:text-field-placeholder";

/** Default empty hint for money fields (`AmountInput`, `OrderCurrencyField`). Override per field when needed. */
export const FORM_CONTROL_MONEY_PLACEHOLDER = "0.00";

/** Default empty hint for quantity fields when the caller omits `placeholder`. */
export const FORM_CONTROL_QUANTITY_PLACEHOLDER = "Qty";

/** Default focus ring (offset) — Input, Select, Textarea. */
export const FORM_CONTROL_FOCUS_RING_CLASS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-field-ring focus-visible:ring-offset-2";

/** Inline / card-embedded controls — inset ring avoids layout shift. */
export const FORM_CONTROL_FOCUS_RING_INLINE_CLASS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-field-ring focus-visible:ring-inset";

/** Composite field shell (TagInput, multi-value inputs). */
export const FORM_CONTROL_FOCUS_WITHIN_CLASS =
  "focus-within:outline-none focus-within:ring-2 focus-within:ring-field-ring focus-within:ring-offset-2";

/** Validation error — red border + matching focus ring. */
export const FORM_CONTROL_INVALID_CLASS =
  "aria-[invalid=true]:border-field-border-invalid aria-[invalid=true]:focus-visible:ring-field-ring-invalid";

export const FORM_CONTROL_DISABLED_CLASS =
  "disabled:cursor-not-allowed disabled:bg-field-disabled disabled:border-field-disabled-border disabled:text-field-disabled-foreground";

/**
 * Standalone field chrome for textarea / custom inputs that don't use `<Input>`.
 * Import this instead of duplicating border-input / bg-background / placeholder classes.
 */
export const FORM_CONTROL_STANDALONE_CLASS = [
  FORM_CONTROL_SURFACE_CLASS,
  FORM_CONTROL_PLACEHOLDER_CLASS,
  FORM_CONTROL_FOCUS_RING_CLASS,
  FORM_CONTROL_INVALID_CLASS,
  FORM_CONTROL_DISABLED_CLASS,
].join(" ");

/** Helper copy below a field (hints, character counts). */
export const FORM_FIELD_HELPER_CLASS = "text-sm text-field-helper";

/** Inline validation message below a field. */
export const FORM_FIELD_ERROR_CLASS = "text-sm text-field-error";

/** Select / combobox panel surface. */
export const DROPDOWN_PANEL_CLASS =
  "border border-dropdown-border bg-dropdown text-dropdown-foreground";

/** Combobox search input inside an open panel. */
export const DROPDOWN_SEARCH_SURFACE_CLASS =
  "border-field-border bg-dropdown-search text-field-foreground";

/**
 * Kill Button hover-lift / active-press / hover fill so form triggers match
 * Input/Select (no grey wash on hover). Apply on every Button-based form
 * control (DatePicker, SearchableSelect, CatalogAsyncSelect, etc.).
 *
 * Use `bg-card` (white) — not `bg-background` (page tint) — so fields on
 * white cards don’t show a grey-blue wash.
 */
export const FORM_CONTROL_TRIGGER_MOTION_CLASS =
  "bg-card active:scale-100 hover:translate-y-0 hover:shadow-none shadow-none hover:bg-card";

/**
 * Date field button trigger in forms (Sales Pipeline dialogs, etc.).
 * Prefer `<DatePicker />` when possible; use this when wiring Popover+Button manually.
 */
export const FORM_CONTROL_DATE_TRIGGER_CLASS = [
  FORM_CONTROL_SURFACE_CLASS,
  FORM_CONTROL_HEIGHT_CLASS,
  "w-full justify-start px-3 font-normal shadow-none",
  FORM_CONTROL_TRIGGER_MOTION_CLASS,
].join(" ");

/**
 * Inner search field inside a combobox/popover — no focus ring.
 * The outer trigger keeps the Input-matching ring; this stays quiet.
 */
export const DROPDOWN_SEARCH_INPUT_CLASS =
  "focus-visible:ring-0 focus-visible:ring-offset-0";

/**
 * Standard dropdown option highlight — `--hover` (Light Gray 100).
 * Use these full class strings so Tailwind JIT always emits the utility.
 */
export const DROPDOWN_ITEM_HIGHLIGHT_CLASS = "bg-hover";
export const DROPDOWN_ITEM_HIGHLIGHT_HOVER_CLASS = "hover:bg-hover";
export const DROPDOWN_ITEM_HIGHLIGHT_FOCUS_CLASS = "focus:bg-hover";
export const DROPDOWN_ITEM_HIGHLIGHT_DATA_HIGHLIGHTED_CLASS =
  "data-[highlighted]:bg-hover";
export const DROPDOWN_ITEM_HIGHLIGHT_DATA_SELECTED_CLASS =
  "data-[selected=true]:bg-hover";
export const DROPDOWN_ITEM_HIGHLIGHT_DATA_CHECKED_CLASS =
  "data-[state=checked]:bg-hover";

/**
 * Dropdown option row base (SelectItem / CommandItem / custom lists).
 */
export const DROPDOWN_ITEM_CLASS =
  "relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50";
